/**
 * Error Boundary Component
 * 
 * Catches React rendering errors and:
 * 1. Logs them to the user_error_log table
 * 2. Shows a user-friendly fallback UI
 * 3. Provides recovery options
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { logErrorBoundary } from '../../lib/logging';
import { Sentry } from '../../lib/sentry';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Check if we're in development mode
 */
function isDev(): boolean {
  try {
    return (import.meta as ImportMeta & { env?: { DEV?: boolean } }).env?.DEV === true;
  } catch {
    return false;
  }
}

export class AppErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logErrorBoundary(error, errorInfo);
    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });
    this.props.onError?.(error, errorInfo);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const showDevDetails = isDev() && this.state.error;

      // Default fallback UI (dark theme — required by design system)
      return (
        <div className="min-h-screen flex items-center justify-center bg-black p-4">
          <div className="bg-black/[0.03] rounded-2xl shadow-xl p-8 max-w-md text-center border border-[#D4D4D4]">
            <div className="w-16 h-16 bg-[#FF453A]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8 text-[#FF453A]" />
            </div>

            <h2 className="text-2xl font-bold text-[#1A1A1A] mb-3">
              Something went wrong
            </h2>

            <p className="text-[#3D3D3D] mb-6">
              We've logged this issue and are working on it.
              Try refreshing the page or going back home.
            </p>

            {/* Error details (dev only) */}
            {showDevDetails && (
              <details className="text-left bg-black/[0.03] p-4 rounded-lg border border-[#D4D4D4] mb-6">
                <summary className="font-medium text-[#3D3D3D] cursor-pointer">
                  Error Details (dev)
                </summary>
                <pre className="mt-2 text-xs text-[#888888] whitespace-pre-wrap break-all overflow-auto max-h-40">
                  {this.state.error?.toString()}
                </pre>
              </details>
            )}

            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleGoHome}
                className="flex items-center gap-2 px-5 py-2.5 bg-black/[0.05] text-[#1A1A1A] rounded-xl font-medium hover:bg-black/[0.06] transition-colors"
              >
                <Home className="w-4 h-4" />
                Go Home
              </button>
              <button
                onClick={this.handleReload}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#0A84FF] text-[#1A1A1A] rounded-xl font-medium hover:bg-[#0A84FF]/80 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AppErrorBoundary;
